import { useWallet, truncateAddress } from '@/hooks/useWallet';

interface WalletButtonProps {
  className?: string;
}

/**
 * Wallet connection button for the header/navbar
 * Shows "Connect Wallet" when disconnected, address when connected
 */
export function WalletButton({ className = '' }: WalletButtonProps): JSX.Element {
  const {
    isConnected,
    isConnecting,
    isRejected,
    isNotExist,
    isError,
    address,
    username,
    walletIcon,
    connect,
    openView,
  } = useWallet();

  const handleClick = (): void => {
    if (isConnected) {
      openView();
    } else {
      void connect();
    }
  };

  // Determine button content based on status
  const getButtonContent = (): { text: string; variant: string } => {
    if (isConnecting) {
      return { text: 'Connecting...', variant: 'connecting' };
    }
    if (isConnected) {
      const displayName = username ?? truncateAddress(address);
      return { text: displayName, variant: 'connected' };
    }
    if (isRejected) {
      return { text: 'Reconnect', variant: 'error' };
    }
    if (isNotExist) {
      return { text: 'Install Wallet', variant: 'error' };
    }
    if (isError) {
      return { text: 'Error - Retry', variant: 'error' };
    }
    return { text: 'Connect Wallet', variant: 'disconnected' };
  };

  const { text, variant } = getButtonContent();

  const baseClasses = 'inline-flex items-center px-2 sm:px-4 py-2 rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses: Record<string, string> = {
    disconnected: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    connected: 'bg-green-100 text-green-800 hover:bg-green-200 focus:ring-green-500 border border-green-300',
    connecting: 'bg-gray-100 text-gray-500 cursor-wait',
    error: 'bg-red-100 text-red-800 hover:bg-red-200 focus:ring-red-500 border border-red-300',
  };

  return (
    <button
      onClick={handleClick}
      disabled={isConnecting}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
    >
      {isConnecting && (
        <svg
          className="animate-spin -ml-1 sm:mr-2 h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {isConnected && walletIcon !== undefined && walletIcon !== '' && (
        <img
          src={walletIcon}
          alt="wallet"
          className="w-4 h-4 sm:mr-2 rounded"
        />
      )}
      {!isConnecting && !isConnected && (
        <svg
          className="w-4 h-4 sm:mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      )}
      <span className="hidden sm:inline">{text}</span>
    </button>
  );
}
